import React, { useContext, useState, useEffect, useMemo, useCallback, memo, useRef } from 'react';
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  StepIconProps,
  styled,
  useTheme,
  useMediaQuery,
  Button,
} from '@mui/material';

import { Check, Trophy, Dumbbell, FileCheck, FileCheck2 } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

import sessionContext from '../../context/sessionContext';
import useFetch from '../../hooks/useFetch';
import { API_USER_SMART } from '../../constants/api';

// Mover constantes para fora do componente para evitar recriação
const STEPS = [
  { label: 'Testes', icon: <FileCheck size={25} strokeWidth={1.5} />, subtitle: 'Você está aqui' },
  { label: 'Treino', icon: <Dumbbell size={25} strokeWidth={1.5} /> },
  { label: 'Reavaliação', icon: <FileCheck2 size={25} strokeWidth={1.5} /> },
  { label: 'Conclusão', icon: <Trophy size={25} strokeWidth={1.5} /> }
] as const;

// Mover styled components para fora e otimizar
const CustomConnector = styled(StepConnector)(() => ({
  '&.MuiStepConnector-alternativeLabel': {
    top: 24,
    left: 'calc(-50% + 16px)',
    right: 'calc(50% + 16px)',
  },
  '& .MuiStepConnector-line': {
    height: 3,
    border: 0,
    backgroundColor: 'transparent',
    borderRadius: 1,
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: '-30px',
      left: 0,
      right: 0,
      height: '40px',
      border: 'none',
      borderTop: '3px dashed rgba(255, 255, 255, 0.6)',
      borderRadius: '50%/20px',
      transform: 'translateY(20px)',
    }
  },
}));

// Memoizar o componente CustomStepIconRoot
const CustomStepIconRoot = memo(styled('div')<{
  active?: boolean;
  completed?: boolean;
  isCurrentStep?: boolean;
  isCompleted?: boolean;
}>(({ active, completed, isCurrentStep, isCompleted }) => {
  const getOpacity = () => {
    if (isCompleted || isCurrentStep) return 1;
    return 0.4;
  };

  const shouldHaveGreenBorder = isCurrentStep && !isCompleted;

  return {
    backgroundColor: active || completed ? '#FFFFFF' : 'rgba(255, 255, 255, 0.3)',
    zIndex: 1,
    color: active || completed ? '#086B7B' : '#FFFFFF',
    width: 48,
    height: 48,
    display: 'flex',
    borderRadius: '50%',
    justifyContent: 'center',
    alignItems: 'center',
    border: shouldHaveGreenBorder
      ? '2px solid #086B7B'
      : active || completed
        ? 'none'
        : '2px solid rgba(255, 255, 255, 0.5)',
    boxShadow: active || completed ? '0 2px 8px rgba(255, 255, 255, 0.2)' : 'none',
    opacity: getOpacity(),
    transition: 'all 0.3s ease-in-out',
    ...(shouldHaveGreenBorder && {
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2), 0 0 0 2px rgb(255, 255, 255)',
    }),
  };
}));

// Memoizar CustomStepIcon
const CustomStepIcon = memo(function CustomStepIcon(props: StepIconProps & {
  activeStep: number;
  journeyCode: number | null;
}) {
  const { active, completed, icon, activeStep, journeyCode } = props;
  const _journeyCode = journeyCode !== 4 ? journeyCode : 1; 
  const stepIndex = Number(icon) - 1;
  const step = journeyCode !== 4 ? STEPS[stepIndex] : STEPS[1]; 

  // Memoizar cálculos
  const isCompleted = useMemo(() =>
    _journeyCode != null && _journeyCode >= stepIndex + 1, 
    [_journeyCode, stepIndex] 
  );

  const isCurrentStep = useMemo(() =>
    activeStep === stepIndex,
    [activeStep, stepIndex]
  );

  return (
    <CustomStepIconRoot
      active={active}
      completed={completed}
      isCurrentStep={isCurrentStep}
      isCompleted={isCompleted}
    >
      {isCompleted ? (
        <Check size={24} strokeWidth={2.5} />
      ) : (
        step.icon
      )}
    </CustomStepIconRoot>
  );
});

const CustomStepLabel = memo(styled(StepLabel)<{
  isCurrentStep?: boolean;
  isCompleted?: boolean;
}>(({ theme, isCurrentStep, isCompleted }) => ({
  '& .MuiStepLabel-label': {
    color: '#FFFFFF',
    fontWeight: '600',
    fontSize: '1rem',
    marginTop: '12px',
    opacity: (isCompleted || isCurrentStep) ? 1 : 0.6,
    transition: 'opacity 0.3s ease-in-out',
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.875rem',
    },
  },
  '& .MuiStepLabel-label.Mui-active': {
    color: '#FFFFFF',
    fontWeight: 'bold',
  },
  '& .MuiStepLabel-label.Mui-completed': {
    color: '#FFFFFF',
  },
})));

// Função helper memoizada
const formatDateMemo = (dateString: string | undefined): string => {
  if (!dateString) return '';

  try {
    const dateParts = dateString.split('-');
    const day = dateParts[2]?.split(' ')[0];
    const month = dateParts[1];
    const year = dateParts[0];

    return `${day}/${month}/${year}`;
  } catch (error) {
    console.error('Erro ao formatar data:', error);
    return '';
  }
};

const Dashboard = memo(() => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const cSession = useContext(sessionContext);

  // Refs para tracking de mudanças
  const previousJourneyCode = useRef<number | null>(null);
  const isInitialMount = useRef(true);
  
  // Estado local otimizado para journey code - agora inicializa como null
  const [currentJourneyCode, setCurrentJourneyCode] = useState<number | null>(null);

  const [isShowingReports, setIsShowingReports] = useState(() => {
    return sessionStorage.getItem('showReports') === 'true';
  });

  const [userInfo, , isLoadingUserInfo, getSmartUserFetch]: any = useFetch({
    autoInit: false,
  });

  // Sistema otimizado de detecção de mudanças no journey_code - agora usa userInfo
  useEffect(() => {
    const newJourneyCode = userInfo?.journey_code ?? null;
    
    // Verifica se houve mudança real no journey_code
    if (previousJourneyCode.current !== newJourneyCode) {
      
      // Só atualiza se não for o mount inicial ou se houve mudança real
      if (!isInitialMount.current || (isInitialMount.current && newJourneyCode !== null)) {
        // console.log(`Journey Code atualizado: ${previousJourneyCode.current} → ${newJourneyCode}`);
        
        // Atualiza o estado local
        setCurrentJourneyCode(newJourneyCode !== 4 ? newJourneyCode : 1); 
      }
      
      // Atualiza a referência
      previousJourneyCode.current = newJourneyCode;
      isInitialMount.current = false;
    }
  }, [userInfo?.journey_code]);

  // Memoizar valores computados usando o estado local
  const activeStep = useMemo(() => {
    if (currentJourneyCode == null || currentJourneyCode < 0) return 0;
    if (currentJourneyCode >= 3) return 3;
    return currentJourneyCode;
  }, [currentJourneyCode]);

  const isInTraining = useMemo(() =>
    currentJourneyCode === 1,
    [currentJourneyCode]
  );

  const shouldShowTrainingInfo = useMemo(() =>
    isInTraining && userInfo?.date_IA_finish,
    [isInTraining, userInfo?.date_IA_finish]
  );

  const formattedDate = useMemo(() =>
    formatDateMemo(userInfo?.date_IA_finish),
    [userInfo?.date_IA_finish]
  );

  // Função otimizada para buscar informações do usuário
  const getUserSmartInfo = useCallback(async (): Promise<void> => {
    try {
      const user = cSession.user;
      if (user && user.token) {
        getSmartUserFetch({
          method: "GET",
          url: API_USER_SMART,
          noAuth: true,
          header: { userauthorization: user.token },
          onSuccess: (data: any) => {
            // console.log('Informações do usuário atualizadas com sucesso');
          },
          onError: (error: any) => {
            console.error('Erro ao buscar informações do usuário:', error);
          }
        });
      }
    } catch (error) {
      console.error('Erro inesperado ao buscar informações:', error);
    }
  }, [cSession.user, getSmartUserFetch]);

  // console.log("SMART_TOKEN", userInfo?.journey_code)
  // console.log("SMART_TOKEN_COMPLETO", userInfo)

  // Função memoizada para botão
  const handleButtonClick = useCallback((): void => {
    const newState = !isShowingReports;
    setIsShowingReports(newState);
    sessionStorage.setItem('showReports', String(newState));

    if (newState) {
      navigate('/relatorio');
    } else {
      navigate('/home');
    }
  }, [isShowingReports, navigate]);

  const getButtonText = useCallback(() =>
    isShowingReports ? 'JOGOS' : 'VER RELATÓRIOS',
    [isShowingReports]
  );

  // Effect que roda no mount e sempre que a rota ou token mudam
  useEffect(() => {
    // Só continua se o usuário estiver logado (token válido)
    if (cSession.user?.token) {
      // Se for a primeira vez que o componente monta
      if (isInitialMount.current) {
        // Busca as informações do usuário pela primeira vez
        getUserSmartInfo();

        // Marca que o mount já aconteceu para não repetir essa lógica
        isInitialMount.current = false;
      } else {
        // Se não for o mount inicial, ou seja, quando a rota mudar depois
        // Também busca as informações do usuário para manter os dados atualizados
        getUserSmartInfo();
      }
    }
  }, [location.pathname, cSession.user?.token, getUserSmartInfo]); // Executa quando a rota, token ou função mudam

  // Memoizar estilos inline
  const containerStyles = useMemo(() => ({
    backgroundColor: '#086B7B',
    minHeight: { xs: '30vh', md: '35vh', xl: '15vh' },
    p: { xs: 2, sm: 3, md: 4 },
    color: 'white',
    background: 'linear-gradient(135deg, #086B7B 0%, #0A7A8C 100%)',
  }), []);

  const stepperStyles = useMemo(() => ({
    '& .MuiStep-root': {
      padding: { xs: '0 4px', sm: '0 8px', md: '0 16px' },
    }
  }), []);

  const buttonStyles = useMemo(() => ({
    color: 'rgba(9, 113, 133, 1)',
    backgroundColor: 'rgba(255, 255, 255, 1)',
    fontSize: '0.75rem',
    px: isMobile ? 3 : 2,
    py: isMobile ? 1 : 0.5,
    transition: 'all 0.3s ease-in-out',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.86)',
      transform: 'translateY(-1px)',
    }
  }), [isMobile]);

  // Debug logs - considere remover em produção
  // if (process.env.NODE_ENV === 'development') {
  //   console.log("JOURNEY CODE ATUAL:", currentJourneyCode);
  //   console.log("ACTIVE STEP CALCULADO:", activeStep);
  //   console.log("PERFIL:", cSession.user?.registry);
  //   console.log("PERFIL COMPLETO:", cSession);
  //   console.log("DATA DE TÉRMINO:", userInfo?.date_IA_finish);
  //   console.log("ESTADO ATUAL - Mostrando relatórios:", isShowingReports);
  // }

  // Condição para ocultar o Dashboard quando estiver em /testes/ com algo depois da barra
  if (location.pathname.match(/\/testes\/.+/)) {
    return null;
  }

  return (
    <Box sx={containerStyles}>
      <Box sx={{ maxWidth: '1200px', margin: '0 auto' }}>
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
          flexDirection: { xs: 'column', md: 'row' },
          mb: { xs: 1, md: 0 }
        }}>
          <Box>
            <Typography
              variant={isMobile ? "h5" : "h4"}
              fontWeight="bold"
              gutterBottom
              sx={{
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' },
                mb: 1,
                display: "flex",
                alignItems: "center",
                flexWrap: "nowrap",
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis" 
              }}
            >
              Olá,&nbsp;
              <Box
                component="span"
                sx={{
                  fontWeight: "inherit",
                  fontSize: "inherit",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  maxWidth: { xs: '55%', sm: '95%' },
                  display: "inline-block"
                }}
              >
                {cSession.user?.registry}
              </Box>
            </Typography>
            <Typography
              variant="subtitle1"
              gutterBottom
              sx={{
                fontSize: { xs: '1rem', md: '1.125rem' },
                mb: { xs: 3, md: 4 },
                opacity: 0.95
              }}
            >
              Confira seu andamento:
            </Typography>
          </Box>

          {shouldShowTrainingInfo && !isMobile && (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              gap: 1
            }}>
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.875rem',
                  opacity: 0.9,
                  fontWeight: '500'
                }}
              >
                Seu treino acaba em {formattedDate}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={handleButtonClick}
                sx={buttonStyles}
              >
                {getButtonText()}
              </Button>
            </Box>
          )}
        </Box>

        <Box sx={{ mt: { xs: 4, md: 6 }, px: { xs: 1, sm: 2 } }}>
          <Stepper
            activeStep={activeStep}
            alternativeLabel
            connector={<CustomConnector />}
            sx={stepperStyles}
          >
            {STEPS.map((step, index) => {
              const isCurrentStep = activeStep === index;
              const isCompleted = currentJourneyCode != null && currentJourneyCode >= index + 1;

              return (
                <Step key={`${step.label}-${index}`}>
                  <CustomStepLabel
                    StepIconComponent={(props) => (
                      <CustomStepIcon
                        {...props}
                        activeStep={activeStep}
                        journeyCode={currentJourneyCode}
                      />
                    )}
                    isCurrentStep={isCurrentStep}
                    isCompleted={isCompleted}
                  >
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography
                        sx={{
                          color: 'white',
                          fontWeight: isCurrentStep ? 'bold' : '600',
                          fontSize: { xs: '0.875rem', md: '1rem' },
                          lineHeight: 1.2,
                          mb: isCurrentStep ? 0.5 : 0,
                          opacity: (isCompleted || isCurrentStep) ? 1 : 0.6,
                          transition: 'opacity 0.3s ease-in-out',
                        }}
                      >
                        {step.label}
                      </Typography>
                      {isCurrentStep && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'rgba(255, 255, 255, 0.9)',
                            fontSize: { xs: '0.75rem', md: '0.875rem' },
                            fontWeight: '400',
                            display: 'block',
                            lineHeight: 1.2
                          }}
                        >
                          Você está aqui
                        </Typography>
                      )}
                    </Box>
                  </CustomStepLabel>
                </Step>
              );
            })}
          </Stepper>

          {shouldShowTrainingInfo && isMobile && (
            <Box sx={{
              mt: 4,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.875rem',
                  opacity: 0.9,
                  fontWeight: '500',
                  textAlign: 'center'
                }}
              >
                Seu treino acaba em {formattedDate}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={handleButtonClick}
                sx={buttonStyles}
              >
                {getButtonText()}
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
});

Dashboard.displayName = 'Dashboard';

export default Dashboard;